<script lang="ts">
import { captureException } from '@sentry/browser';
import {
  enableTrackingFunction,
  enableTrackingShopFunction,
  getPriceForTrackingFunction,
  isShopFunction,
  trackAddMultipleToCartFunction,
  type TrackingEventItem,
} from 'composables/tracking';
import GET_CATALOG_PRODUCT_DATA_BY_ID from 'gql/getCatalogProductDataById.gql';
import eventHubMixin from 'mixins/eventHubMixin.vue';

// @ts-ignore
const GET_BESTPRICE = () => import('gql/getBestPrice.gql');

type StringKeyedObject = {
  [key: string]: any;
};

export default {
  mixins: [eventHubMixin],
  computed: {
    isShop(): boolean {
      return isShopFunction(this.$fischer);
    },
    brandName(): string {
      return this.$fischer.brandName();
    },
    enableTracking(): boolean {
      return enableTrackingFunction(this.$fischer);
    },
    enableTrackingShop(): boolean {
      return enableTrackingShopFunction(this.$fischer);
    },
    pageType(): string {
      return window.location.pathname;
    },
  },
  methods: {
    trackViewItemList(productList: any[], isPDP: boolean): void {
      try {
        if (!this.enableTracking) {
          return;
        }

        const items: TrackingEventItem[] = [];
        productList.forEach((product: any, index: number) => {
          if (!product) {
            return;
          }

          const isFamily = product.CategoryRole === 'FAMILY_PAGE';
          const item: TrackingEventItem = {
            item_name: product.name || product.ProductName || product.CategoryName,
            item_id: isFamily ? product.id || 'FP' : product.ProductId || product.id,
            item_brand: this.brandName,
            item_list_name: isPDP ? 'accessories' : 'Featured Items',
            item_list_id: isPDP ? 'A-PDP' : this.pageType,
            index: index + 1,
            ...this.getCategories(product.CategoryDisplayNamePath),
          };

          items.push(item);
        });

        if (items.length) {
          this.addToDataLayer('view_item_list', items);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackViewItem(data: any, isFamily: boolean): Promise<void> {
      try {
        if (!this.enableTracking) {
          return;
        }

        const ecomLang = isFamily
          ? data.TextList.find((el: any) => el.Key === 'BEZEICHNUNG')?.Value
          : data.TextList.find((el: any) => el.Key === 'ECOMLANG')?.Value;
        const itemId = isFamily ? data.CategoryId : data.ProductId;

        let item: TrackingEventItem = {
          item_name: ecomLang,
          item_id: itemId,
          item_brand: this.brandName,
          ...this.getCategories(data.CategoryDisplayNamePath),
        };

        if (!isFamily) {
          item.item_variant = data.ProductName;
        }

        const { price, quantity, currency, totalPrice } = await this.getPriceForTracking(
          data.ProductId,
        );
        if (price) {
          item.price = price;
          item.quantity = quantity;
        }

        let ecommerce;
        if (totalPrice) {
          ecommerce = {
            currency: currency,
            value: totalPrice,
          };
        }

        this.addToDataLayer('view_item', [item], ecommerce);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackSelectItem(product: any, isPDP: boolean): Promise<void> {
      try {
        if (!this.enableTracking) {
          return;
        }

        const isFamily = product.CategoryRole === 'FAMILY_PAGE';
        if (!product.CategoryRole || isFamily) {
          const item: TrackingEventItem = {
            item_name: product.name || product.CategoryName,
            item_id: isFamily ? product.id || 'FP' : product.id,
            item_brand: this.brandName,
            item_list_name: isPDP ? 'accessories' : 'Featured Items',
            item_list_id: isPDP ? 'A-PDP' : this.pageType,
            index: 1,
            ...this.getCategories(product.CategoryDisplayNamePath),
          };
          this.addToDataLayer('select_item', [item]);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackAddProductToWishlist(product: any): Promise<void> {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const ecomLang = product.TextList.find((el: any) => el.Key === 'ECOMLANG')?.Value;

        const item: TrackingEventItem = {
          item_name: ecomLang,
          item_id: product.ProductId,
          item_brand: this.brandName,
          item_variant: product.ProductName,
          ...this.getCategories(product.CategoryDisplayNamePath),
        };

        const { price, quantity, currency, totalPrice } = await this.getPriceForTracking(
          product.ProductId,
        );
        if (price) {
          item.price = price;
          item.quantity = quantity;
        }

        let ecommerce;
        if (totalPrice) {
          ecommerce = {
            currency: currency,
            value: totalPrice,
          };
        }

        this.addToDataLayer('add_to_wishlist', [item], ecommerce);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackAddCartToWishlist(cart: any): void {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
        };
        if (items.length) {
          this.addToDataLayer('add_to_wishlist', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackViewCart(cart: any): void {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);

        const ecommerce = {
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
        };

        if (items.length) {
          this.addToDataLayer('view_cart', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackAddToCart(
      cartId: number | string,
      productIdUnpadded: string,
      qty: number,
      fromMemoList: boolean,
    ): Promise<void> {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const productId = productIdUnpadded.padStart(6, '0');
        const product = await this.getProductById(productId);
        if (!product) {
          console.info(`product not found with id: ${productId}`);
          return;
        }

        const ecomLang = product.TextList?.find((el: any) => el.Key === 'ECOMLANG')?.Value;

        let item: TrackingEventItem = {
          item_name: ecomLang,
          item_id: productId,
          item_brand: this.brandName,
          item_variant: product.ProductName,
          quantity: qty,
          ...this.getCategories(product.CategoryDisplayNamePath),
        };

        const { price, currency, totalPrice } = await this.getPriceForTracking(productId, qty);
        if (price) {
          item.price = price;
        }

        let ecommerce;
        if (totalPrice) {
          ecommerce = {
            currency: currency,
            value: totalPrice,
          };
        }
        this.addToDataLayer('add_to_cart', [item], ecommerce);
        const items: TrackingEventItem[] = this.$fischer.shop.isClosedShop()
          ? [
              {
                productId: productId,
                quantity: qty,
                productPrice: totalPrice,
                currency,
              },
            ]
          : [
              {
                productId: productId,
              },
            ];

        this.sendEvent(fromMemoList ? 'memo_list_cart_single' : 'add_to_cart', {
          cartId,
          items,
        });
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackAddMultipleToCart(
      cartId: number | string,
      productsWithQuantity: {
        id: string;
        quantity: number;
      }[],
    ): Promise<void> {
      const query = (await GET_BESTPRICE()).default;
      return trackAddMultipleToCartFunction(
        cartId,
        productsWithQuantity,
        this.$fischer,
        (variables) =>
          this.$apollo.query({
            query,
            errorPolicy: 'all',
            variables,
          }),
        (type, data) => this.sendEvent(type, data),
      );
    },
    async trackRemoveFromCart(productList: any): Promise<void> {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = [];
        productList.forEach((product: any) => {
          let item: TrackingEventItem = {
            item_name: product.name,
            item_id: product.articleNo,
            item_brand: this.brandName,
            item_variant: product.catalogProductData?.ProductName,
            quantity: product.quantity,
            ...this.getCategories(product.catalogProductData?.CategoryDisplayNamePath),
          };

          if (product.priceValue && product.initialQuantity) {
            item.price = product.priceValue / product.initialQuantity;
          }

          items.push(item);
        });

        this.addToDataLayer('remove_from_cart', items);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackBeginCheckout(cart: any): void {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
        };

        if (items.length) {
          this.addToDataLayer('begin_checkout', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackPurchase(cart: any): void {
      try {
        if (!this.enableTracking && !this.enableTrackingShop) {
          return;
        }

        const affiliation = `${this.$fischer.hostname()}/${this.$fischer
          .siteLanguage()
          .toLowerCase()}/`;
        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          affiliation,
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
          tax: cart.totalTax?.value,
          shipping: cart.deliveryCost?.value,
          transaction_id: cart.code,
          order_channel: this.isAppView ? 'myfischer online APP' : 'myfischer online',
        };

        if (items.length) {
          this.addToDataLayer('purchase', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackOnlineRetailerClicked(cart: any, retailerName: string): void {
      try {
        if (!this.enableTracking && !this.enableTrackingShop) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          retailerName,
          cartItems: items?.length,
        };

        this.addToDataLayer('RCClickedOnRetailer', items, ecommerce);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    getCartItems(cart: any): TrackingEventItem[] {
      const items: TrackingEventItem[] = [];
      if (cart?.products?.length) {
        cart.products.forEach((product: any) => {
          const productData = product.catalogProductData || {};
          const item: TrackingEventItem = {
            item_id: product.articleNo,
            item_name: product.name,
            item_brand: this.brandName,
            item_variant: productData.ProductName,
            ...this.getCategories(productData.CategoryDisplayNamePath),
          };

          if (this.isShop) {
            item.price = product.priceValue / product.initialQuantity;
            item.quantity = product.quantity;
          }

          items.push(item);
        });
      }

      return items;
    },
    getCategories(paths: string[]): Record<string, string> {
      const categories: StringKeyedObject = {};
      if (paths?.length) {
        paths.forEach((el: any, idx: number) => {
          categories[idx === 0 ? 'item_category' : `item_category${idx + 1}`] = el;
        });
      }
      return categories;
    },
    async getPriceForTracking(
      productIdUnpadded: string,
      qty: number = 0,
    ): Promise<{
      price: number | undefined;
      quantity: number;
      currency: string;
      totalPrice: number | undefined;
    }> {
      const query = (await GET_BESTPRICE()).default;
      return getPriceForTrackingFunction(productIdUnpadded, qty, this.$fischer, (variables) =>
        this.$apollo.query({
          query,
          errorPolicy: 'all',
          variables,
        }),
      );
    },
    async getProductById(productId: string): Promise<any | null> {
      try {
        const { data } = await this.$apollo.query({
          query: GET_CATALOG_PRODUCT_DATA_BY_ID,
          errorPolicy: 'all',
          variables: {
            baseSiteId: this.$fischer.baseSiteId(),
            apiLang: this.$fischer.catalogLocale(),
            id: productId,
          },
        });

        return data.getCatalogProductDataById || null;
      } catch (error) {
        console.error('error getting product data', productId, error);
        return null;
      }
    },
    addToDataLayer(event: string, items: TrackingEventItem[], ecommerce?: object): void {
      if (IS_SSR) {
        return;
      }

      if (typeof window.gtag === 'function') {
        window.gtag('event', event, {
          ...ecommerce,
          items,
        });
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: event,
          ecommerce: {
            ...ecommerce,
            items,
          },
        });
      }
    },
  },
};
</script>
